<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { usePeerLanguageOptions } from '../usePeerLanguageOptions';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const emit = defineEmits(['stay', 'leave']);

const { t } = useI18n();
const store = useStore();
const { options: peerLanguageOptions } = usePeerLanguageOptions();

const prefs = computed(() => store.getters.prefsFor(props.conversationId));
const isTranslating = computed(
  () => store.getters.isTranslatingConversation(props.conversationId)
);

const updatePref = patch => {
  if (isTranslating.value && ('inboundOn' in patch || 'outboundAutoTranslate' in patch)) {
    return;
  }
  store.dispatch('updateConversationPrefs', {
    conversationId: props.conversationId,
    patch,
  }).then(next => {
    if (patch.outboundAutoTranslate === true && next?.outboundAutoTranslate) {
      store.dispatch('batchTranslateConversation', {
        conversationId: props.conversationId,
        direction: 'out',
      });
    }
    if (patch.inboundOn === true && next?.inboundOn) {
      store.dispatch('batchTranslateConversation', {
        conversationId: props.conversationId,
        direction: 'in',
      });
    }
  });
};
</script>

<template>
  <div
    class="w-80 p-3 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg text-sm"
    @mouseenter="emit('stay')"
    @mouseleave="emit('leave')"
  >
    <div class="flex items-center justify-between mb-3">
      <span class="font-semibold text-n-slate-12">{{ t('CUSTOM.TRANSLATION.SETTINGS_TITLE') }}</span>
      <span class="text-[10px] text-n-slate-10 uppercase">AI</span>
    </div>

    <div class="flex items-center justify-between mb-3">
      <div>
        <div class="text-n-slate-12">{{ t('CUSTOM.TRANSLATION.INBOUND') }}</div>
        <div class="text-xs text-n-slate-10">{{ t('CUSTOM.TRANSLATION.INBOUND_HINT') }}</div>
      </div>
      <button
        type="button"
        class="w-9 h-5 rounded-full relative transition-colors focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        :class="[
          prefs.inboundOn ? 'bg-n-brand' : 'bg-n-alpha-2',
          isTranslating ? '' : 'cursor-pointer',
        ]"
        :disabled="isTranslating"
        :aria-pressed="prefs.inboundOn"
        :title="isTranslating ? t('CUSTOM.TRANSLATION.INBOUND_BUSY') : ''"
        @click="updatePref({ inboundOn: !prefs.inboundOn })"
      >
        <span
          class="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all"
          :class="prefs.inboundOn ? 'left-[18px]' : 'left-0.5'"
        />
      </button>
    </div>

    <div class="flex items-center justify-between mb-3">
      <div>
        <div class="text-n-slate-12">{{ t('CUSTOM.TRANSLATION.OUTBOUND_PREVIEW') }}</div>
        <div class="text-xs text-n-slate-10">{{ t('CUSTOM.TRANSLATION.OUTBOUND_PREVIEW_HINT') }}</div>
      </div>
      <button
        type="button"
        class="w-9 h-5 rounded-full relative transition-colors cursor-pointer focus:outline-none"
        :class="prefs.outboundPreviewOn ? 'bg-n-brand' : 'bg-n-alpha-2'"
        :aria-pressed="prefs.outboundPreviewOn"
        @click="updatePref({ outboundPreviewOn: !prefs.outboundPreviewOn })"
      >
        <span
          class="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all"
          :class="prefs.outboundPreviewOn ? 'left-[18px]' : 'left-0.5'"
        />
      </button>
    </div>

    <div class="flex items-center justify-between mb-3">
      <div>
        <div class="text-n-slate-12">{{ t('CUSTOM.TRANSLATION.OUTBOUND_AUTO') }}</div>
        <div class="text-xs text-n-slate-10">{{ t('CUSTOM.TRANSLATION.OUTBOUND_AUTO_HINT') }}</div>
      </div>
      <button
        type="button"
        class="w-9 h-5 rounded-full relative transition-colors focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        :class="[
          prefs.outboundAutoTranslate ? 'bg-n-brand' : 'bg-n-alpha-2',
          isTranslating ? '' : 'cursor-pointer',
        ]"
        :disabled="isTranslating"
        :aria-pressed="prefs.outboundAutoTranslate"
        @click="updatePref({ outboundAutoTranslate: !prefs.outboundAutoTranslate })"
      >
        <span
          class="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all"
          :class="prefs.outboundAutoTranslate ? 'left-[18px]' : 'left-0.5'"
        />
      </button>
    </div>

    <div class="mb-2">
      <label class="block text-xs text-n-slate-11 mb-1">{{ t('CUSTOM.TRANSLATION.SELF_LANG') }}</label>
      <div class="px-2 py-1.5 bg-n-alpha-1 rounded text-n-slate-11">
        {{ t('CUSTOM.TRANSLATION.SELF_LANG_VALUE') }}
      </div>
    </div>

    <div class="mb-2">
      <label class="block text-xs text-n-slate-11 mb-1">{{ t('CUSTOM.TRANSLATION.PEER_LANG') }}</label>
      <select
        class="w-full px-2 py-1.5 bg-n-alpha-1 border border-n-weak rounded text-n-slate-12"
        :value="prefs.peerLang"
        @change="updatePref({ peerLang: $event.target.value })"
      >
        <option
          v-for="opt in peerLanguageOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <div class="text-xs text-n-slate-10 mt-1">{{ t('CUSTOM.TRANSLATION.PEER_LANG_HINT') }}</div>
    </div>
  </div>
</template>
